/**
 * チャット一覧の日時表示フォーマットとセクション分類ユーティリティ。
 * フォーマットは Intl API を使用し、ロケールごとの分岐を排除。
 */

/** 日差分を計算する（ローカルタイムゾーン基準） */
function diffCalendarDays(date: Date, now: Date): number {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const n = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((n.getTime() - d.getTime()) / 86_400_000);
}

/**
 * チャット一覧の日時表示粒度を決定し、フォーマット済み文字列を返す。
 *
 * - 当日: 時刻のみ (例: "14:32")
 * - 昨日: 相対表記 (例: "昨日" / "yesterday")
 * - 2〜7日前: 曜日名 (例: "月曜日" / "Monday")
 * - 8日以前・同年: 月日 (例: "3月26日" / "Mar 26")
 * - 前年以前: 年月日 (例: "2025年12月1日" / "Dec 1, 2025")
 */
export function formatChatDate(
  dateString: string,
  locale: string,
  now?: Date,
): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const ref = now ?? new Date();
  const diffDays = diffCalendarDays(date, ref);

  if (diffDays <= 0) {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }

  if (diffDays === 1) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      -1,
      "day",
    );
  }

  if (diffDays <= 7) {
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
  }

  if (date.getFullYear() === ref.getFullYear()) {
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
    }).format(date);
  }

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/** チャット一覧のセクション分類。today=当日, past_week=1〜7日前, past_month=8〜30日前, older=31日以上前。 */
export type ChatDateSection = "today" | "past_week" | "past_month" | "older";

/** クライアントのタイムゾーンに基づいてセクションを判定する */
export function getChatDateSection(
  dateString: string,
  now?: Date,
): ChatDateSection {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "older";

  const ref = now ?? new Date();
  const diffDays = diffCalendarDays(date, ref);

  if (diffDays <= 0) return "today";
  if (diffDays <= 7) return "past_week";
  if (diffDays <= 30) return "past_month";
  return "older";
}

const sectionLabels: Record<ChatDateSection, { ja: string; en: string }> = {
  today: { ja: "今日", en: "Today" },
  past_week: { ja: "過去1週間", en: "Past week" },
  past_month: { ja: "過去30日間", en: "Past 30 days" },
  older: { ja: "それ以前", en: "Older" },
};

/** セクション種別をロケールに応じた表示ラベルに変換する。ja 以外のロケールは英語にフォールバックする。 */
export function getSectionLabel(
  section: ChatDateSection,
  locale: string,
): string {
  const labels = sectionLabels[section];
  return locale === "ja" ? labels.ja : labels.en;
}
